// Regex patterns for parsing application process
export const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})/;
export const urlRegex = /(https?:\/\/\S+)/g;

// Types
export interface ApplicationDetails {
	link: string | null;
	applicationType: "URL" | "EMAIL" | "NOT_AVAILABLE";
	jobId: string | null;
	companyId: string | null;
}

export interface JobDetails {
	_id?: string;
	jobTitle?: string;
	jobExpertise?: string;
	company?: {
		_id?: string;
		company_name?: string;
		logo_url?: string;
		about_us_summary?: string;
		company_industry?: string;
		company_size_approx?: string;
		company_website?: string;
	};
	jobLocation?: {
		city?: string;
		region?: string;
	};
	startingDate?: string;
	closeAt?: string;
	employmentType?: string;
	salary?: {
		minPay?: number;
		maxPay?: number;
		payCurrency?: string;
		payFrequency?: string;
		payOption?: string;
	};
	jobDescription?: {
		lookingToHire?: string;
		responsibilities?: Array<{ value: string }>;
		qualifications?: Array<{ value: string }>;
	};
	applicationProcess?: string;
	educationQualification?: {
		minEducationLevel?: string;
		relevantMajors?: string[];
	};
}

export interface RoleExplanations {
	role_summary: { en: string; sw: string };
	what_you_will_do: { en: string[]; sw: string[] };
	what_you_need: { en: string[]; sw: string[] };
	who_succeeds: { en: string; sw: string };
	what_it_is: { en: string; sw: string };
	how_to_apply: {
		instructions: { en: string; sw: string };
		include: { en: string[]; sw: string[] };
	};
	cta_fit_check: { en: string; sw: string };
}

/**
 * Capitalize first letter and lowercase the rest
 */
export function toLowerCaseCapitalize(str: string | undefined | null): string {
	if (!str) return "";
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Check if a job posting has expired
 */
export function isJobExpired(jobCloseDate: string | Date | undefined): boolean {
	if (!jobCloseDate) return false;
	const currentDate = new Date();
	const expDate = new Date(jobCloseDate);
	return currentDate > expDate;
}

interface JobDataForApplication {
	applicationProcess?: string;
	_id?: string;
	company?: { _id?: string };
}

/**
 * Extract application details (email or URL) from job posting
 */
export function getApplicationDetails(
	jobData: JobDataForApplication,
	fallbackJobDetails: JobDataForApplication = {},
): ApplicationDetails {
	const { applicationProcess, _id, company } = jobData;

	if (!applicationProcess) {
		return { link: null, applicationType: "NOT_AVAILABLE", jobId: null, companyId: null };
	}

	// Extract email
	const emailMatch = applicationProcess.match(emailRegex);

	// Extract URLs
	const urlMatches = applicationProcess.match(urlRegex);

	if (urlMatches) {
		return {
			link: urlMatches[0],
			applicationType: "URL",
			jobId: _id || fallbackJobDetails._id || null,
			companyId: company?._id || fallbackJobDetails.company?._id || null,
		};
	}

	if (emailMatch) {
		return {
			link: emailMatch[0],
			applicationType: "EMAIL",
			jobId: _id || fallbackJobDetails._id || null,
			companyId: null,
		};
	}

	return { link: null, applicationType: "NOT_AVAILABLE", jobId: null, companyId: null };
}

/**
 * Validate that response conforms to RoleExplanations shape
 */
export function isValidRoleExplanations(obj: unknown): obj is RoleExplanations {
	if (!obj || typeof obj !== "object") return false;

	const data = obj as Record<string, unknown>;

	const isString = (v: unknown): v is string => typeof v === "string";
	const isStringArray = (arr: unknown): arr is string[] =>
		Array.isArray(arr) && arr.every((i) => typeof i === "string");

	// role_summary
	const roleSummary = data.role_summary as Record<string, unknown> | undefined;
	if (!roleSummary || !isString(roleSummary.en) || !isString(roleSummary.sw)) return false;

	// role_summary
	const whoSucceeds = data.who_succeeds as Record<string, unknown> | undefined;
	if (!whoSucceeds || !isString(whoSucceeds.en) || !isString(whoSucceeds.sw)) return false;

	// role_summary
	const whatItIs = data.what_it_is as Record<string, unknown> | undefined;
	if (!whatItIs || !isString(whatItIs.en) || !isString(whatItIs.sw)) return false;

	// what_you_will_do
	const whatYouWillDo = data.what_you_will_do as Record<string, unknown> | undefined;
	if (!whatYouWillDo || !isStringArray(whatYouWillDo.en) || !isStringArray(whatYouWillDo.sw)) return false;

	// what_you_need
	const whatYouNeed = data.what_you_need as Record<string, unknown> | undefined;
	if (!whatYouNeed || !isStringArray(whatYouNeed.en) || !isStringArray(whatYouNeed.sw)) return false;

	// how_to_apply
	const howToApply = data.how_to_apply as Record<string, unknown> | undefined;
	if (!howToApply) return false;

	const instructions = howToApply.instructions as Record<string, unknown> | undefined;
	if (!instructions || !isString(instructions.en) || !isString(instructions.sw)) return false;

	const include = howToApply.include as Record<string, unknown> | undefined;
	if (!include || !isStringArray(include.en) || !isStringArray(include.sw)) return false;

	// cta_fit_check
	const ctaFitCheck = data.cta_fit_check as Record<string, unknown> | undefined;
	return !(!ctaFitCheck || !isString(ctaFitCheck.en) || !isString(ctaFitCheck.sw));
}
